// Logyx forms can be opened in a new tab by admin panel purely to run the configurator — build a template, then hand the result
// back and get out of the way. The host keeps its own tab open behind a loader.

import LocalStorageService from 'services/LocalStorageService';
import { LOCAL_STORAGE_BORROWED_SESSION } from 'services/api/constants';

export enum HostMessageTypes {
  TEMPLATE_SAVED = 'TEMPLATE_SAVED',
}

export interface IHostMessage {
  type: HostMessageTypes;
  payload?: Record<string, unknown>;
}

// `window.opener` is set only when another page opened this one with
// window.open. A user who navigated here themselves — the dealer panel flow —
// has none, which is what keeps all of this out of that path.
export const isOpenedFromHostApp = (): boolean => !!window.opener;

// Posted with '*' rather than an allowlist of origins. The payload is a template
// id — useless to anyone without an authenticated session — and the host checks
// event.origin when it receives, which is the direction where trust matters. An
// allowlist here would mean a build-time variable per environment, and forms
// silently not closing the tab whenever it was unset or slightly mistyped.
export const postHostMessage = (message: IHostMessage): void => {
  window.opener?.postMessage(message, '*');
};

// The JWT handed over in the URL was borrowed for this tab only. It belongs to an
// admin, who has no selected company, so leaving it behind breaks the user's next
// ordinary visit to forms — they land on a blank page. localStorage is per-origin
// and outlives the tab, so it has to be cleared on the way out.
//
// Guarded by the marker rather than by "is this the admin tab": if the host never
// passed a token, the session in localStorage is the user's own and must be left
// alone.
export const clearBorrowedSession = (): void => {
  if (LocalStorageService.getItem(LOCAL_STORAGE_BORROWED_SESSION) !== 'true') {
    return;
  }
  LocalStorageService.clear();
};

// Closing is allowed here because the window was opened by a script.
export const closeHostAppTab = (): void => {
  window.close();
};
